import type { Squad } from '@gonfalon/types/Squad';

import { createExperienceTracker } from './createExperienceTracker';
import { trackWithDetails } from './trackWithDetails';
import type { AnalyticEventData } from './types';

declare const __DEV__: boolean;

export interface LegacyMetricNames {
  /** Legacy event name for the Start lifecycle event */
  start?: string;
  /** Map of track event name → legacy metric name */
  track?: Record<string, string>;
  /** Legacy event name for the End lifecycle event */
  end?: string;
}

/** Specifiy a positive, neutral or negative outcome */
type EndOutcome = 'success' | 'error' | 'cancel';

export interface EndOptions extends AnalyticEventData {
  outcome: EndOutcome;
}

/** Keys which are added to every event by the underlying track utility -
 * don't pass these keys as attributes to track calls otherwise they'll be overwritten */
type BaseUtilityAttributes = {
  url?: never;
  path?: never;
  fullstorySession?: never;
};
/** Keys managed internally by the tracker that callers should not provide. */
type ManagedStartAttributes = {
  experienceName?: never;
  experienceId?: never;
  msFromDocumentLoad?: never;
} & BaseUtilityAttributes;
type ManagedTrackAttributes = {
  experienceName?: never;
  experienceId?: never;
  msFromStart?: never;
} & BaseUtilityAttributes;
type ManagedEndAttributes = { experienceName?: never; experienceId?: never; duration?: never } & BaseUtilityAttributes;
type AllManagedAttributes = ManagedStartAttributes & ManagedTrackAttributes & ManagedEndAttributes;

/**
 * An event in the tracker's generic can be a plain string name,
 * or an object with `eventName` and optional typed `attributes`.
 *
 * @example
 * // Plain strings (existing behavior)
 * type Events = 'Step Completed' | 'Form Submitted';
 *
 * // Mixed: some events with typed attributes
 * type Events =
 *   | 'Step Completed'
 *   | { eventName: 'Form Submitted'; attributes: { formId: string } };
 */
export type EventConfig = string | { eventName: string; attributes?: AnalyticEventData & AllManagedAttributes };

type ExtractEventName<T extends EventConfig> = T extends { eventName: infer N extends string }
  ? N
  : T extends string
    ? T
    : never;

type ExtractEventAttributes<T extends EventConfig, TName extends string> = [Extract<T, { eventName: TName }>] extends [
  never,
]
  ? AnalyticEventData
  : Extract<T, { eventName: TName }> extends { attributes: infer A }
    ? A
    : AnalyticEventData;

export interface Tracker<TEventDef extends EventConfig = string> {
  start: (attributes?: AnalyticEventData & ManagedStartAttributes) => void;
  track: <TName extends ExtractEventName<TEventDef>>(
    name: TName,
    attributes?: ExtractEventAttributes<TEventDef, TName> & ManagedTrackAttributes,
  ) => void;
  end: (options: EndOptions & ManagedEndAttributes) => void;
}

export interface ExperienceStateEntry {
  experienceName: string;
  experienceId: string;
  owners?: Squad[];
  msFromDocumentLoad: number;
  duration: number;
}

export interface CreateOptions extends AnalyticEventData {
  /** Optional category attached to all tracking calls for this experience */
  category?: string;
  /** Optional team owners for error attribution */
  owners?: Squad[];
  /** Whether to emit legacy metrics for backward compatibility */
  emitLegacyMetrics?: boolean;
  /** Legacy metric names to emit when emitLegacyMetrics is true */
  legacyMetricNames?: LegacyMetricNames;
}

export interface ExperienceTrackerInterface {
  create: <TEventDef extends EventConfig = string>(
    experienceName: string,
    options?: CreateOptions,
  ) => Tracker<TEventDef>;
  getState: () => ExperienceStateEntry[];
  clear: (experienceName: string) => void;
  endAll: (options?: EndOptions) => void;
}

/**
 * Tracks multi-step user experiences as a funnel with Start, intermediate, and End events.
 * Each event is automatically namespaced under the experience name and enriched with
 * `experienceId`, `msFromDocumentLoad` (only start events), `msFromStart` (only track events), and `duration` (only end events).
 *
 * @example
 * // Typed events — metric data source editor
 * type MDSEditorEvent =
 *   | 'Toggle Accordion'
 *   | { eventName: 'Preview SQL Submitted'; attributes: { valid: boolean } }
 *   | 'Preview SQL Success'
 *   | { eventName: 'Preview SQL Error'; attributes: { sqlQuery: string } }
 *   | 'Form Submission';
 *
 * export const createMDSTracker = ExperienceTracker.create<MDSEditorEvent>(
 *   'Create Metric Data Source',
 *   { owners: ['team-agent-platform'], category: 'Metrics' },
 * );
 *
 * createMDSTracker.track('Preview SQL Submitted', { valid: true }); // typed ✓
 * createMDSTracker.track('Preview SQL Submitted', { valid: 123 });  // type error ✗
 *
 */
export const ExperienceTracker: ExperienceTrackerInterface = createExperienceTracker({
  emit: trackWithDetails,
  warn: (message) => {
    if (__DEV__) {
      // oxlint-disable-next-line no-console
      console.warn(message);
    }
  },
});
